/**
 * Scene Duration Calculator
 * 
 * Calculates the precise total duration of a scene based on:
 * - Dialog timings, from the shared timing model (`sceneTiming.ts`, spec 007)
 * - Sound `endTime`s (a sound without one does not lengthen the scene)
 * - Scene enter/exit transitions
 */

import type { Story } from '../@types';
import { computeDialogTimeline } from './sceneTiming';

/**
 * Calculate the precise total duration of a scene
 * 
 * @param scene - The scene data
 * @returns Duration in milliseconds
 */
export function calculateSceneDuration(
  scene: Pick<Story, 'dialogs' | 'sounds' | 'exitTransition'> | null,
): number {
  if (!scene) return 0;
  
  let maxEndTime = 0;
  
  // Calculate max end time from dialogs
  for (const dialog of scene.dialogs || []) {
    const endTime = computeDialogTimeline(dialog).endMs;
    if (endTime > maxEndTime) {
      maxEndTime = endTime;
    }
  }
  
  // Calculate max end time from sounds
  for (const sound of scene.sounds || []) {
    const endTime = sound.endTime;
    if (typeof endTime === 'number' && endTime > maxEndTime) {
      maxEndTime = endTime;
    }
  }
  
  // Add scene exit transition duration (enter transition is at start, doesn't extend duration)
  const exitTransition = scene.exitTransition;
  if (exitTransition && exitTransition.type !== 'none' && exitTransition.duration) {
    maxEndTime += exitTransition.duration;
  }
  
  // Minimum duration of 1 second if scene has no content
  return Math.max(maxEndTime, 1000);
}
